import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Shield, User, Hash, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface UserRegistrationProps {
  onRegistrationComplete: (userData: { 
    id: string;
    name: string; 
    team: string;
    playerId: string;
    jerseyNumber: number;
    skillLevel?: string;
  }) => void;
}

interface Player {
  id: string;
  full_name: string;
  team: string;
  jersey_number: number;
  skill_level: string;
}

export const UserRegistration: React.FC<UserRegistrationProps> = ({ 
  onRegistrationComplete 
}) => {
  const [name, setName] = useState("");
  const [jerseyNumber, setJerseyNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: name, 2: jersey number
  const [playerMatch, setPlayerMatch] = useState<Player | null>(null);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch all players on component mount
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const { data: players, error } = await supabase
          .from('players')
          .select('id, full_name, team, jersey_number, skill_level')
          .eq('is_active', true)
          .order('full_name');

        if (error) throw error;
        
        setAllPlayers(players || []);
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };

    fetchPlayers();
  }, []);

  // Filter players based on name input
  useEffect(() => {
    if (name.trim().length >= 1) {
      const searchTerm = name.trim().toLowerCase();
      const filtered = allPlayers.filter(player =>
        player.full_name.toLowerCase().includes(searchTerm) ||
        player.full_name.toLowerCase().split(' ').some(namePart => 
          namePart.startsWith(searchTerm)
        )
      );
      // Sort by best match (starts with search term first)
      filtered.sort((a, b) => {
        const aStartsWith = a.full_name.toLowerCase().startsWith(searchTerm);
        const bStartsWith = b.full_name.toLowerCase().startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return a.full_name.localeCompare(b.full_name);
      });
      setFilteredPlayers(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredPlayers([]);
      setShowSuggestions(false);
    }
  }, [name, allPlayers]);

  const handleNameSelect = (selectedPlayer: Player) => {
    setName(selectedPlayer.full_name);
    setShowSuggestions(false);
    setPlayerMatch(selectedPlayer);
    setStep(2);
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "👋 Hey there!",
        description: "Please type your name so we can find you!",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Check for exact name match in database - only registered players allowed
      const { data: players, error } = await supabase
        .from('players')
        .select('id, full_name, team, jersey_number, skill_level')
        .eq('is_active', true)
        .ilike('full_name', name.trim());

      if (error) {
        throw error;
      }

    const exactMatch = players?.find(player => 
      player.full_name.toLowerCase() === name.trim().toLowerCase()
    );

    if (exactMatch) {
      setPlayerMatch(exactMatch);
      setStep(2); // Move to jersey number verification
      } else {
        toast({
          title: "🤔 Hmm, I can't find you!",
          description: "Try clicking on your name from the list above, or ask a grown-up for help!",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Name verification error:', error);
      toast({
        title: "Verification Error",
        description: "Unable to verify player registration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJerseySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!jerseyNumber.trim()) {
      toast({
        title: "🎽 Jersey Number!",
        description: "Please type the number on your shirt!",
        variant: "destructive",
      });
      return;
    }

    if (!playerMatch) {
      toast({
        title: "Authentication Error",
        description: "Player information not found. Please start over.",
        variant: "destructive",
      });
      setStep(1);
      return;
    }

    const enteredNumber = parseInt(jerseyNumber.trim());

    // For clubs allowing duplicate jerseys, we need to handle potential ambiguity
    if (isNaN(enteredNumber)) {
      toast({
        title: "Invalid Jersey Number",
        description: "Please enter a valid number.",
        variant: "destructive",
      });
      return;
    }

    // Check if the entered jersey number matches any player with the same name
    const { data: possibleMatches, error: checkError } = await supabase
      .from('players')
      .select('*')
      .eq('full_name', playerMatch.full_name)
      .eq('team', playerMatch.team)
      .eq('jersey_number', enteredNumber)
      .eq('is_active', true);

    if (checkError) {
      console.error('Error checking jersey number:', checkError);
      toast({
        title: "Authentication Error",
        description: "Unable to verify jersey number. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (!possibleMatches || possibleMatches.length === 0) {
      toast({
        title: "Authentication Failed", 
        description: "Invalid jersey number. Access denied.",
        variant: "destructive",
      });
      return;
    }

    // Use the first match (or the exact match if unique)
    const finalPlayerMatch = possibleMatches[0];

    setLoading(true);

    try {
      // Ensure profile exists by calling get_or_create_profile
      const { data: profileData, error: profileError } = await supabase
        .rpc('get_or_create_profile', {
          p_player_id: finalPlayerMatch.id,
          p_name: finalPlayerMatch.full_name,
          p_team: finalPlayerMatch.team
        });

      if (profileError) {
        console.error('Profile creation error:', profileError);
        toast({
          title: "Profile Setup Error",
          description: "Unable to set up your profile. Please try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('🎯 REGISTRATION DEBUG: Profile data retrieved:', {
        profileData,
        playerMatch: {
          id: finalPlayerMatch.id,
          full_name: finalPlayerMatch.full_name,
          team: finalPlayerMatch.team,
          jersey_number: finalPlayerMatch.jersey_number,
          skill_level: finalPlayerMatch.skill_level
        }
      });

      // Authentication successful - complete registration with profile ID
      const userData = {
        id: profileData || '', // Use the profile ID returned from the function
        name: finalPlayerMatch.full_name,
        team: finalPlayerMatch.team,
        playerId: finalPlayerMatch.id,
        jerseyNumber: finalPlayerMatch.jersey_number,
        skillLevel: finalPlayerMatch.skill_level
      };

      console.log('🎯 REGISTRATION DEBUG: Calling onRegistrationComplete with:', userData);
      onRegistrationComplete(userData);

      toast({
        title: "🎉 Welcome!",
        description: `Hi ${playerMatch.full_name}! Ready to start training?`,
      });
    } catch (error) {
      console.error('Registration completion error:', error);
      toast({
        title: "Registration Error",
        description: "Unable to complete registration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleBackToName = () => {
    setStep(1);
    setPlayerMatch(null);
    setJerseyNumber("");
  };

  return (
    <Card className="w-full bg-tech-surface/95 backdrop-blur-md border border-tech-border/50 shadow-2xl relative z-10">
        <CardHeader className="text-center space-y-6">
          <CardTitle className="text-3xl font-bold text-white mb-4">
            🏆 Player Sign In
          </CardTitle>
          <p className="text-tech-glow/90 text-2xl font-semibold">
            {step === 1 
              ? "👋 Hi! What's your name?" 
              : "🎽 What's your jersey number?"
            }
          </p>
          <div className="text-lg text-tech-glow/70 bg-tech-surface/30 rounded-lg p-4 border border-tech-border/30">
            <div className="font-bold text-xl">Step {step} of 2</div>
            <div className="text-base mt-1">
              {step === 1 
                ? "Type your name and pick it from the list!" 
                : "Almost done! Just your jersey number."
              }
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {step === 1 ? (
            <form onSubmit={handleNameSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-tech-glow/60" />
                  <Input
                    type="text"
                    placeholder="Start typing your name..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-16 bg-tech-surface/50 border-tech-border text-white placeholder:text-tech-glow/60 focus:border-tech-accent focus:ring-2 focus:ring-tech-accent/30 text-xl py-8 font-semibold"
                    disabled={loading}
                    autoComplete="off"
                  />
                  
                  {/* Name Suggestions Dropdown */}
                  {showSuggestions && filteredPlayers.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-tech-surface border border-tech-border rounded-lg shadow-2xl z-50 max-h-64 overflow-y-auto animate-in fade-in-0 slide-in-from-top-2 duration-200">
                      <div className="py-2">
                        {filteredPlayers.slice(0, 8).map((player, index) => (
                          <button
                            key={player.id}
                            type="button"
                            onClick={() => handleNameSelect(player)}
                            className="w-full px-4 py-3 text-left hover:bg-tech-primary/30 active:bg-tech-primary/40 transition-all duration-150 group focus:outline-none focus:bg-tech-primary/30"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className="text-white font-semibold text-lg group-hover:text-tech-glow transition-colors">
                                  {player.full_name}
                                </span>
                                <span className="text-tech-glow/70 text-sm group-hover:text-tech-glow/90 transition-colors">
                                  {player.team}
                                </span>
                              </div>
                              <div className="text-tech-accent/60 group-hover:text-tech-accent transition-colors">
                                <User className="h-5 w-5" />
                              </div>
                            </div>
                          </button>
                        ))}
                        {filteredPlayers.length > 8 && (
                          <div className="px-4 py-2 text-center text-tech-glow/60 text-sm border-t border-tech-border/30">
                            {filteredPlayers.length - 8} more players... Keep typing to narrow down
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-base text-tech-glow/80 px-2 font-medium">
                    ✨ Start typing your name and select it from the dropdown! 
                  </p>
                  <p className="text-sm text-tech-glow/60 px-2 mt-1">
                    {name.length > 0 && filteredPlayers.length === 0 && name.length >= 2 
                      ? "🔍 No matches found - try a different spelling"
                      : "Just type a few letters and your name will appear below"
                    }
                  </p>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-tech-primary to-tech-secondary hover:from-tech-glow hover:to-tech-accent text-white font-bold py-6 text-xl transition-all duration-300 hover:scale-105 rounded-lg" 
                disabled={loading || !name.trim()}
              >
                  {loading ? "🔍 Looking for you..." : "🎯 Find My Name"}
                </Button>
              </form>
            ) : (
            <form onSubmit={handleJerseySubmit} className="space-y-6">
              <div className="space-y-6">
                <div className="p-6 bg-tech-accent/10 rounded-lg text-center border border-tech-accent/20">
                  <p className="text-lg text-tech-glow/90 mb-2 font-semibold">🎉 Great job!</p>
                  <p className="font-bold text-white text-2xl">{playerMatch?.full_name}</p>
                  <p className="text-base text-tech-glow/70 mt-1">{playerMatch?.team}</p>
                </div>
                
                <div className="relative">
                  <Hash className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-tech-glow/60" />
                  <Input
                    type="number"
                    placeholder="Type your jersey number..."
                    value={jerseyNumber}
                    onChange={(e) => setJerseyNumber(e.target.value)}
                    className="pl-16 bg-tech-surface/50 border-tech-border text-white placeholder:text-tech-glow/60 focus:border-tech-accent focus:ring-2 focus:ring-tech-accent/30 text-xl py-8 font-semibold text-center"
                    disabled={loading}
                    min="1"
                  />
                </div>
                <div className="text-center">
                  <p className="text-base text-tech-glow/80 px-2 font-medium">
                    🏃‍♂️ Look at the number on your shirt!
                  </p>
                  <p className="text-sm text-tech-glow/60 px-2 mt-1">
                    Type that number to finish signing in
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-tech-primary to-tech-secondary hover:from-tech-glow hover:to-tech-accent text-white font-bold py-6 text-xl transition-all duration-300 hover:scale-105 rounded-lg" 
                  disabled={loading || !jerseyNumber.trim()}
                >
                  {loading ? "🔍 Checking..." : "🚀 Start Training!"}
                </Button>
                
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full bg-tech-surface/30 border-tech-border text-white hover:bg-tech-primary/20 py-4 text-lg font-semibold" 
                  onClick={handleBackToName}
                  disabled={loading}
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  ← Back to Name
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
  );
};